'use client';

import { createContext, RefObject } from 'react';

import { BaseAuthMembership } from './types';

export interface BaseAuthUser {
  avatar?: string;
  created_at?: string;
  email: string;
  event_identifiers?: {
    [key: string]: string;
  };
  group_identifier?: string;
  id: string; // CloudQuery Database UID
  name?: string;
  onboarded?: boolean;
  registered_team_internal?: boolean;
  registered_team_name?: string;
}

export interface BaseAuthContextType {
  currentMembership: BaseAuthMembership | null;
  error: any;
  isAuthenticated: boolean;
  isInitialized: boolean;
  isLoading: boolean;
  loggedInRef: RefObject<boolean>;
  manuallySignedOutRef: RefObject<boolean>;
  memberships: BaseAuthMembership[];
  provider: string | null;
  redirectSuccess: boolean;
  refresh: () => Promise<void>;
  samlEndpoint?: string | null;
  setCurrentMembership: (membership: BaseAuthMembership) => void;
  setError: (error: any) => void;
  setIsLoading: (isLoading: boolean) => void;
  setLogoutState: () => void;
  setProvider: (provider: string) => void;
  setRedirectSuccess: (redirectSuccess: boolean) => void;
  signIn: (token: string) => Promise<void>;
  signOut: () => Promise<void>;
  user: BaseAuthUser | null;
  userMfaConfigured?: boolean;
  userMfaRequired?: boolean;
}

export const initialAuthState: BaseAuthContextType = {
  currentMembership: null,
  error: null,
  isAuthenticated: false,
  isInitialized: false,
  isLoading: false,
  loggedInRef: { current: false },
  manuallySignedOutRef: { current: false },
  memberships: [],
  provider: null,
  redirectSuccess: false,
  refresh: () => Promise.resolve(),
  setCurrentMembership: () => {},
  setError: () => {},
  setIsLoading: () => {},
  setLogoutState: () => {},
  setProvider: () => {},
  setRedirectSuccess: () => {},
  signIn: () => Promise.resolve(),
  signOut: () => Promise.resolve(),
  user: null,
  userMfaConfigured: undefined,
  userMfaRequired: undefined,
};

export const logoutAuthState = {
  currentMembership: null,
  error: null,
  isAuthenticated: false,
  isInitialized: true,
  isLoading: false,
  memberships: [],
  provider: null,
  redirectSuccess: false,
  user: null,
};

export const BaseAuthContext = createContext<BaseAuthContextType>({
  ...initialAuthState,
  loggedInRef: { current: false },
  manuallySignedOutRef: { current: false },
  refresh: () => Promise.resolve(),
  setCurrentMembership: () => {},
  setError: () => {},
  setIsLoading: () => {},
  setLogoutState: () => {},
  setProvider: () => {},
  setRedirectSuccess: () => {},
  signIn: () => Promise.resolve(),
  signOut: () => Promise.resolve(),
});
